/**
 * useCampaignReportingDeepLink — makes the reporting sub-tab driven by the
 * `?sub` search param, with the URL as the single source of truth (mirrors
 * useCampaignDeepLinkTab for the main tab).
 *
 *   tab=reporting&sub=sentiment         -> Sentiment Intelligence
 *   tab=reporting&sub=audienceDynamics  -> Audience Dynamics
 *   tab=reporting&sub=creatorContent    -> Creator Content
 *   tab=reporting&sub=conversions       -> Shopify conversions
 *   tab=reporting&sub=overview | absent -> General Insights (default)
 *
 * Returns `{ sub, setSub }`:
 * - `sub` is the resolved sub-tab, or null when `tab` isn't 'reporting' or
 *   `sub` is absent/unknown — the surface then falls back to its 'overview'
 *   default. A refresh or a shared link reopens the exact reporting view.
 * - `setSub(next)` navigates to write `?sub`, so clicking a sub-tab persists it.
 *   The functional updater preserves any other params; `as never` is used for
 *   the same router-agnostic reason as useCampaignDeepLinkTab (this hook lives
 *   in `@discovr/ui` and can't reference the app's registered route tree).
 */
import { useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { CampaignSearch } from './search';

// Mirrors the SUB_TABS ids in CampaignOverviewTab. Kept local to avoid coupling
// this resolver to the heavy reporting component.
export type ReportingSubTab =
  | 'overview'
  | 'sentiment'
  | 'audienceDynamics'
  | 'creatorContent'
  | 'conversions';

const REPORTING_SUB_TABS: readonly ReportingSubTab[] = [
  'overview',
  'sentiment',
  'audienceDynamics',
  'creatorContent',
  'conversions',
];

const isReportingSubTab = (value: string | undefined): value is ReportingSubTab =>
  value !== undefined && (REPORTING_SUB_TABS as readonly string[]).includes(value);

export function useCampaignReportingDeepLink(): {
  sub: ReportingSubTab | null;
  setSub: (next: ReportingSubTab) => void;
} {
  const { tab, sub } = useSearch({ strict: false }) as Partial<CampaignSearch>;
  const navigate = useNavigate();
  const resolved = tab === 'reporting' && isReportingSubTab(sub) ? sub : null;
  const setSub = useCallback(
    (next: ReportingSubTab) =>
      navigate({
        to: '.',
        search: (prev: Partial<CampaignSearch>) => ({ ...prev, tab: 'reporting', sub: next }),
      } as never),
    [navigate],
  );
  return { sub: resolved, setSub };
}
